/* ============================================================
 * HAPPYFORCE DESIGN SYSTEM — Tokens
 *
 * Single source of truth for colors, type, spacing, radii, and
 * shadows. Mirrors the design bundle delivered at
 *   `happyforce-design-system/project/colors_and_type.css`.
 *
 * Consumers:
 *  - `hfMuiTheme.ts`        wires palette + typography from here
 *  - `layoutConstants.ts`   re-exports a subset for legacy imports
 *  - `color.utilities.js`   re-sources its named exports from here
 *  - `style.utils.js`       re-sources chart palettes from here
 *  - any `.tsx` `sx={}`     reads `theme.palette.hfXxx` (preferred)
 *                           or imports `HF_*` directly
 *  - any `.css/.scss`       reads matching CSS custom properties
 *                           declared in `designTokens.css`
 *
 * Keep the values in lockstep with `designTokens.css`.
 * ============================================================ */

/* ----------  BRAND  ---------- */
export const HF_BRAND_COLORS = {
    yellow: '#FFC300',
    yellowTint: '#FFF9E5',
    black: '#000000',
    white: '#FFFFFF'
} as const;

/* ----------  MOOD FACES (happiness scale)  ---------- */
export const HF_MOOD_COLORS = {
    excellent: '#FFC400',
    good: '#15C8A0',
    bad: '#6B70E0',
    verybad: '#FF3A66'
} as const;

/* ----------  CTA / PRIMARY BLUE (interactive scale)  ---------- */
export const HF_CTA_COLORS = {
    700: '#254871',
    600: '#376CAA',
    500: '#4A90E2',
    300: '#A4C7F1',
    200: '#D2E3F8',
    100: '#EDF4FC'
} as const;

/* ----------  NEUTRALS (text + surfaces)  ---------- */
export const HF_NEUTRAL_COLORS = {
    700: '#1A2231',
    600: '#344563',
    500: '#67748A',
    400: '#99A2B1',
    300: '#CCD1D8',
    250: '#D7DADE',
    200: '#EBECEF',
    100: '#F3F4F7',
    '000': '#FBFBFB'
} as const;

/* ----------  SEMANTIC STATES  ---------- */
export const HF_SEMANTIC_COLORS = {
    success: { 600: '#1F7A5C', 500: '#00C196', 100: '#D4F1E8' },
    warning: { 600: '#B36B00', 500: '#FFA000', 100: '#FFE9CC' },
    error:   { 700: '#800E0E', 500: '#FF1C1C', 200: '#FFC6C6', 100: '#FFEDED' }
} as const;

/* ----------  CHART / COMPLEMENTARY PALETTE  ----------
 * Production hex values currently shipped in `style.utils.js`.
 * They drift slightly from the pristine design-system mood/brand
 * tokens (e.g. `#FFC200` here vs `#FFC300` in HF_BRAND_COLORS).
 * Charts read THESE values to preserve existing visuals — the
 * brand/mood tokens above are reserved for new UI surfaces.
 */
export const HF_CHART_COLORS = {
    yellow:     '#FFC200',  // legacy production yellow (charts, nav accent)
    yellow200:  '#FFF0BF',
    green:      '#71CA00',
    green200:   '#DBF2BF',
    greenAlt:   '#0CAD00',  // score line / strong positive
    greenLight: '#96D600',  // recognitions
    olive:      '#8FC246',  // question / recognition palette
    oliveDark:  '#7B955D',
    blue:       '#4A90E2',
    blue200:    '#D2E3F8',
    blueAlt:    '#4B89D2',
    blueLink:   '#0079FF',
    blueBright: '#00A3FF',
    purple:     '#6C70DC',
    purple200:  '#DADBF6',
    purpleAlt:  '#B330D3',
    turquoise:  '#37C098',
    turquoise200:'#CDEFE5',
    turquoiseAlt:'#3EBFDE',
    orange:     '#FF9300',
    orange200:  '#FFE4BF',
    orangeAlt:  '#EC7F4A',
    orangeBright:'#FF3700',
    pink:       '#DF356B',
    pink200:    '#F7CDDA',
    pinkDark:   '#B50C71',
    red:        '#FF1C1C',
    red200:     '#FFC6C6',
    redAlt:     '#D92C32',
    redDark:    '#C63A0E',
    brown:      '#A87446',
    tan:        '#ECD3BB',  // criticism
    grayWarm:   '#A0A0A0',
    grayLight:  '#C8CBCC',  // neutral non-relevant
    grayMid:    '#9F9F9F',
    grayDark:   '#2D3133',
    grayDarker: '#303233',
    grayWhite:  '#F2F2F2',
    grayPale:   '#EEEEEE',
    grayDropdown: '#626366',
    /* Face colors used in legacy sentiment scales */
    faceHappy:  '#FFBA18',
    faceGood:   '#37C098',
    faceBad:    '#6C70DC',
    faceSad:    '#D92C32',
    /* "Non-relevant" muted variants */
    goodMuted:    '#B8E580',
    warningMuted: '#FFC980',
    badMuted:     '#FF8D8D',
    /* Extended question / recognition palette */
    teal:       '#44C751',
    /* Coral red used by HI evolution chart */
    coral:      '#FF5F3D',
    /* Slate gray-blue used as gridline color in some legacy charts */
    slate:      '#6991AB',
    /* Border / divider variants used by legacy chart axes */
    borderSoft: '#E0E0E0',
    borderTone: '#B5BBC3',
    chartBg:    '#F0F1FB'
} as const;

/* ----------  SPACING (4-pt scale)  ---------- */
export const HF_SPACING = {
    /** Off-grid 2px — used inside very small chips/icons. Avoid for layout. */
    tiny: 2,
    xs:  4,
    /** Off-grid 6px — used by some chip insets / dot legend gutters. */
    xsPlus: 6,
    sm:  8,
    /** Off-grid 10px — used in a few legacy "tight" layouts. */
    smPlus: 10,
    md:  12,
    lg:  16,
    /** Off-grid 20px — used in some card padding. Prefer `lg` (16) or `xl` (24) on the grid. */
    lgPlus: 20,
    xl:  24,
    /** Off-grid 28px — used in some hero spacing. */
    xlPlus: 28,
    xxl: 32,
    xxxl:40,
    huge:48,
    mega:64
} as const;

/* ----------  RADIUS  ---------- */
export const HF_RADIUS = {
    sm:    2,
    md:    4,
    lg:    5,
    pill:  12,
    /** Fully rounded — use for chips/buttons that should be capsule-shaped. */
    full:  9999,
    circle: '50%'
} as const;

/* ----------  SHADOWS  ---------- */
export const HF_SHADOWS = {
    card: '0 4px 12px -4px rgba(26,34,49,.10)',
    pop:  '0 8px 24px -6px rgba(26,34,49,.18)',
    soft: '0 1px 3px rgba(26,34,49,.06)'
} as const;

/* ----------  FONT FAMILIES  ---------- */
export const HF_FONT_FAMILIES = {
    /** Brand / display — self-hosted at src/assets/fonts/ */
    brand:  '"Source Sans Pro", system-ui, sans-serif',
    /** Product UI body (matches production dashboard) */
    ui:     "'Helvetica-Light', open sans, 'Helvetica Neue', Helvetica, Arial, sans-serif",
    /** Helvetica-only stack used inside the MUI theme typography variants */
    uiPlain:'Helvetica, "Helvetica Neue", Arial, sans-serif',
    /** Secondary accent (Barlow via Google Fonts) */
    accent: '"Barlow", "Source Sans Pro", sans-serif'
} as const;

/* ----------  FONT SIZES (px)  ---------- */
export const HF_FONT_SIZES = {
    bigNumber: 60,
    bigKpi:    48,   /* secondary big-number (used in compact KPI cards) */
    h1:        24,
    h2:        20,
    h3:        18,
    /** Body legacy 17px used in some onboarding/welcome surfaces. */
    bodyLg:    17,
    body:      16,
    small:     14,
    /** Legacy 13px used by the default document body (HF_BODY_STYLES.fontSizePx). */
    bodySm:    13,
    mini:      12,
    xs:        11,
    tiny:      10
} as const;

/* ----------  LINE HEIGHTS (px)  ---------- */
export const HF_LINE_HEIGHTS = {
    bigNumber: 69,
    h1:        36,
    h2:        30,
    h3:        28,
    body:      24,
    bodyTight: 20,
    small:     16,
    mini:      14
} as const;

/* ----------  FONT WEIGHTS  ---------- */
export const HF_FONT_WEIGHTS = {
    extraLight: 200,
    light:      300,
    regular:    400,
    medium:     500,
    semibold:   600,
    bold:       700,
    extraBold:  800,
    black:      900
} as const;

/* ----------  Z-INDEX  ---------- */
export const HF_Z_INDEX = {
    /** Stack above MUI defaults — matches existing MuiPopper/MuiPopover overrides */
    overlay: 5000
} as const;

/* ----------  ICON SIZES (px)  ----------
 * For MUI icons (<SomeIcon sx={{ fontSize: HF_ICON_SIZES.md }} />),
 * ImageIcon, and inline SVGs. Align with typography: xs≈caption, sm≈body3.
 */
export const HF_ICON_SIZES = {
    xs:     12,
    sm:     16,
    md:     18,
    lg:     20,
    xl:     24,
    xxl:    32,
    xxxl:   48
} as const;

/** Pair inline MUI icons with adjacent typography variant. */
export type HfTypographyVariant =
    | 'caption'
    | 'body3'
    | 'body2'
    | 'body1'
    | 'h6'
    | 'h5'
    | 'h4'
    | 'h3'
    | 'h2'
    | 'h1';

export const HF_ICON_SIZE_BY_TYPOGRAPHY: Record<HfTypographyVariant, number> = {
    caption: HF_ICON_SIZES.sm,
    body3:   HF_ICON_SIZES.sm,
    body2:   HF_ICON_SIZES.md,
    body1:   HF_ICON_SIZES.md,
    h6:      HF_ICON_SIZES.md,
    h5:      HF_ICON_SIZES.lg,
    h4:      HF_ICON_SIZES.lg,
    h3:      HF_ICON_SIZES.xl,
    h2:      HF_ICON_SIZES.xl,
    h1:      HF_ICON_SIZES.xxl
};

/** ImageIcon size key aligned with typography (for non-inline stat/hero icons). */
export const HF_IMAGE_ICON_SIZE_BY_TYPOGRAPHY: Record<HfTypographyVariant, keyof typeof HF_ICON_SIZES> = {
    caption: 'sm',
    body3:   'sm',
    body2:   'md',
    body1:   'md',
    h6:      'md',
    h5:      'lg',
    h4:      'lg',
    h3:      'xl',
    h2:      'xxl',
    h1:      'xxxl'
};

/** sx for MUI SvgIcon placed next to Typography — e.g. `<EditIcon sx={hfInlineIconSx('body3')} />` */
export const hfInlineIconSx = (typographyVariant: HfTypographyVariant = 'body3') => ({
    fontSize: HF_ICON_SIZE_BY_TYPOGRAPHY[typographyVariant]
});

/** Icon stacked above a count in compact stat tiles (dashboard widgets, KPI rows). */
export const HF_STAT_TILE_ICON_SIZE = HF_ICON_SIZES.xl;

export const HF_STAT_TILE_IMAGE_ICON: keyof typeof HF_ICON_SIZES = 'xl';

export const hfStatTileIconSx = {
    fontSize: HF_STAT_TILE_ICON_SIZE
};

/* ----------  BORDERS / STROKE WIDTHS  ---------- */
export const HF_BORDER_WIDTHS = {
    /** Hairline divider (default) */
    thin:    1,
    /** Standard form input border */
    base:    1,
    /** Slightly heavier divider for emphasis */
    medium:  2,
    /** Chart line / focus ring */
    line:    2,
    /** Chart series lines */
    chart:   3,
    /** Hover / pressed emphasis */
    thick:   4
} as const;

/** Composed border shorthands (style consumed as `border: HF_BORDERS.divider`) */
export const HF_BORDERS = {
    divider:       `1px solid ${HF_NEUTRAL_COLORS[200]}`,
    dividerStrong: `1px solid ${HF_NEUTRAL_COLORS[300]}`,
    input:         `1px solid ${HF_NEUTRAL_COLORS[300]}`,
    inputFocus:    `1px solid ${HF_CTA_COLORS[500]}`,
    none:          'none'
} as const;

/* ----------  Convenience  ---------- */
export const HF_TEXT_COLORS = {
    primary:   HF_NEUTRAL_COLORS[700],
    secondary: '#999999',
    body:      '#2d3133',
    disabled:  'rgba(0, 0, 0, 0.38)'
} as const;
